import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Map as MapIcon, Search, Filter, RefreshCw } from 'lucide-react';
import MapView from '../components/clientMap/MapView';
import ClientList from '../components/clientMap/ClientList';
import UnresolvedPanel from '../components/clientMap/UnresolvedPanel';
import AddressModal from '../components/clientMap/AddressModal';
import {
  MAP_GROUPS, KRAJE, NO_KRAJ, EMPTY_FILTERS, filterClients, computeCounts,
  displayGroup, modelOptions, needsAttention,
} from '../utils/clientMap';

const API = '/api/client-map';
const card = { backgroundColor: '#FFF', borderRadius: '12px', border: '1px solid #E0E0E0' };
const labelStyle = { display: 'block', fontSize: '13px', fontWeight: 500, color: '#333', marginBottom: '6px' };
const selectStyle = { padding: '10px 12px', border: '1px solid #E0E0E0', borderRadius: '8px', fontSize: '14px', backgroundColor: '#FFF', minWidth: '150px' };

const parseCoord = (text) => {
  const n = parseFloat(String(text).replace(',', '.'));
  return Number.isFinite(n) ? n : null;
};
const fmt = (n) => (n == null ? '' : n.toFixed(6));
// Customer address and customer site have separate location endpoints.
const placePath = (c) => (c.type === 'site' ? `${API}/sites/${c.site_id}` : `${API}/${c.id}`);

async function apiJson(url, options) {
  const res = await fetch(url, { credentials: 'include', headers: { 'Content-Type': 'application/json' }, ...options });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.success === false) {
    throw new Error(json.details?.[0]?.msg || json.error || `Chyba ${res.status}`);
  }
  return json;
}

const Counter = ({ label, value, sub, color }) => (
  <div style={{ ...card, padding: '12px 16px', flex: '1 1 140px' }}>
    <div style={{ fontSize: '12px', color: '#666', display: 'flex', alignItems: 'center', gap: '6px' }}>
      {color && <span aria-hidden style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: color }} />}{label}
    </div>
    <div style={{ fontSize: '22px', fontWeight: 700, color: '#333' }}>{value}</div>
    {sub && <div style={{ fontSize: '12px', color: '#999' }}>{sub}</div>}
  </div>
);

const ClientMap = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [focus, setFocus] = useState(null);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editError, setEditError] = useState('');
  const [showKraje, setShowKraje] = useState(false);
  const [krajeAvailable, setKrajeAvailable] = useState(true);
  const [addressEntry, setAddressEntry] = useState(null);
  const [notice, setNotice] = useState(null); // { ok: boolean, text: string }

  const load = useCallback(async () => {
    setLoadError('');
    try {
      const json = await apiJson(API);
      setClients(json.data || []);
    } catch (err) {
      setLoadError(`Nepodařilo se načíst data mapy: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => filterClients(clients, filters), [clients, filters]);
  const counts = useMemo(() => computeCounts(filtered, filters), [filtered, filters]);
  const models = useMemo(() => modelOptions(clients), [clients]);
  const unresolved = useMemo(() => clients.filter(needsAttention), [clients]);
  const groupFor = useCallback((c) => displayGroup(c, filters), [filters.group]); // eslint-disable-line react-hooks/exhaustive-deps

  const setFilter = (key) => (e) => setFilters((f) => ({ ...f, [key]: e.target.value }));
  const hasFilters = Object.values(filters).some(Boolean);

  const selectClient = (c) => { if (c.location) setFocus({ key: c.key, ts: Date.now() }); };

  const startEdit = (c) => {
    setEditError('');
    setEditing({ key: c.key, path: placePath(c), lat: c.location?.lat ?? null, lng: c.location?.lng ?? null, latText: fmt(c.location?.lat), lngText: fmt(c.location?.lng) });
  };
  const moveEdit = useCallback((lat, lng) => setEditing((e) => e && { ...e, lat, lng, latText: fmt(lat), lngText: fmt(lng) }), []);
  const changeEdit = (key, text) => setEditing((e) => {
    const next = { ...e, [`${key}Text`]: text };
    const lat = parseCoord(next.latText);
    const lng = parseCoord(next.lngText);
    return lat != null && lng != null ? { ...next, lat, lng } : { ...next, lat: null, lng: null };
  });

  const saveEdit = async () => {
    setBusy(true); setEditError('');
    try {
      await apiJson(`${editing.path}/location`, { method: 'PUT', body: JSON.stringify({ lat: editing.lat, lng: editing.lng }) });
      setEditing(null);
      await load();
    } catch (err) {
      setEditError(`Uložení se nezdařilo: ${err.message}`);
    } finally { setBusy(false); }
  };

  const openAddress = useCallback((key) => {
    setNotice(null);
    setAddressEntry(clients.find((c) => c.key === key) || null);
  }, [clients]);

  const addressSaved = async (key, geocodeStatus) => {
    setAddressEntry(null);
    await load();
    const texts = {
      ok: { ok: true, text: 'Adresa uložena a bod na mapě přesunut.' },
      unchanged: { ok: true, text: 'Adresa uložena (beze změny polohy).' },
    };
    setNotice(texts[geocodeStatus] || { ok: false, text: 'Adresa uložena, ale na mapě ji nelze najít. Zkontrolujte ji nebo bod umístěte ručně v panelu výše.' });
    setFocus({ key, ts: Date.now() });
  };

  const retry = async (c) => {
    setBusy(true); setEditError('');
    try {
      const json = await apiJson(`${placePath(c)}/geocode`, { method: 'POST' });
      if (json.data.status !== 'ok') setEditError(`${c.name}: adresu se ani napodruhé nepodařilo najít. Umístěte bod ručně.`);
      await load();
    } catch (err) {
      setEditError(`Geokódování se nezdařilo: ${err.message}`);
    } finally { setBusy(false); }
  };

  return (
    <div style={{ padding: '24px' }} className="client-map-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', gap: '12px', flexWrap: 'wrap' }}>
        <h1 style={{ fontSize: '24px', fontWeight: 700, color: '#333', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
          <MapIcon color="#E53935" /> Mapa klientů
        </h1>
        <button type="button" onClick={load} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 16px', backgroundColor: '#FFF', color: '#333', border: '1px solid #E0E0E0', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' }}>
          <RefreshCw size={16} /> Obnovit
        </button>
      </div>

      {/* Filters */}
      <div style={{ ...card, padding: '16px 20px', marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <Filter size={18} color="#E53935" />
          <h2 style={{ fontSize: '15px', fontWeight: 600, color: '#333', margin: 0 }}>Filtry</h2>
          {hasFilters && (
            <button type="button" onClick={() => setFilters(EMPTY_FILTERS)} style={{ marginLeft: 'auto', padding: '4px 8px', fontSize: '12px', backgroundColor: '#F5F5F5', color: '#666', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
              Vymazat filtry
            </button>
          )}
        </div>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'end' }}>
          <div style={{ flex: '1 1 240px' }}>
            <label htmlFor="cm-search" style={labelStyle}>Vyhledávání</label>
            <div style={{ position: 'relative' }}>
              <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#999', pointerEvents: 'none' }} />
              <input id="cm-search" type="search" placeholder="Název, město, IČO…" value={filters.search} onChange={setFilter('search')}
                style={{ width: '100%', padding: '10px 10px 10px 40px', border: '1px solid #E0E0E0', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }} />
            </div>
          </div>
          <div>
            <label htmlFor="cm-group" style={labelStyle}>Stav</label>
            <select id="cm-group" value={filters.group} onChange={setFilter('group')} style={selectStyle}>
              <option value="">Všechny stavy</option>
              {MAP_GROUPS.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="cm-model" style={labelStyle}>Model</label>
            <select id="cm-model" value={filters.model} onChange={setFilter('model')} style={selectStyle}>
              <option value="">Všechny modely</option>
              {models.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="cm-kraj" style={labelStyle}>Kraj</label>
            <select id="cm-kraj" value={filters.kraj} onChange={setFilter('kraj')} style={selectStyle}>
              <option value="">Všechny kraje</option>
              {KRAJE.map((k) => <option key={k} value={k}>{k}</option>)}
              <option value={NO_KRAJ}>Neurčeno</option>
            </select>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: krajeAvailable ? '#333' : '#999', paddingBottom: '10px' }}
            title={krajeAvailable ? '' : 'Soubor s hranicemi krajů zatím není nahraný'}>
            <input type="checkbox" checked={showKraje && krajeAvailable} disabled={!krajeAvailable} onChange={(e) => setShowKraje(e.target.checked)} />
            Hranice krajů
          </label>
        </div>
      </div>

      {/* Counters */}
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' }} aria-live="polite">
        <Counter label="Klientů" value={counts.clients} sub={`${counts.places} míst · ${counts.machines} trenažérů`} />
        {MAP_GROUPS.map((g) => <Counter key={g.value} label={g.label} value={counts.byGroup[g.value]} sub="trenažérů" color={g.color} />)}
      </div>

      <UnresolvedPanel clients={unresolved} editing={editing} busy={busy} error={editError}
        onStartEdit={startEdit} onEditAddress={(c) => openAddress(c.key)} onEditChange={changeEdit} onCancel={() => setEditing(null)} onSave={saveEdit} onRetry={retry} />

      {loadError && <p role="alert" style={{ ...card, padding: '12px 16px', color: '#D32F2F', marginBottom: '16px' }}>{loadError}</p>}
      {notice && (
        <p role="status" style={{ ...card, padding: '12px 16px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px', color: notice.ok ? '#2E7D32' : '#E65100', borderColor: notice.ok ? '#C8E6C9' : '#FFE0B2' }}>
          {notice.text}
          <button type="button" onClick={() => setNotice(null)} aria-label="Zavřít" style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: '#999', fontSize: '16px' }}>×</button>
        </p>
      )}
      {addressEntry && <AddressModal entry={addressEntry} onClose={() => setAddressEntry(null)} onSaved={addressSaved} />}

      <div className="client-map-grid">
        <div style={{ ...card, padding: 0, position: 'relative', overflow: 'hidden' }} className="client-map-map">
          {!loading && (
            <MapView clients={filtered} groupFor={groupFor} focus={focus} editing={editing}
              onEditMove={moveEdit} onOpenDetail={(id) => navigate(`/customers/${id}`)} onEditAddress={openAddress}
              showKraje={showKraje && krajeAvailable} onKrajeAvailable={setKrajeAvailable} />
          )}
          <div style={{ position: 'absolute', left: '12px', bottom: '24px', zIndex: 500, backgroundColor: 'rgba(255,255,255,0.95)', borderRadius: '8px', padding: '8px 12px', fontSize: '12px', boxShadow: '0 1px 4px rgba(0,0,0,.2)' }} aria-label="Legenda">
            {MAP_GROUPS.map((g) => (
              <div key={g.value} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span aria-hidden style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: g.color, border: '2px solid #fff', boxShadow: '0 0 0 1px #ccc' }} />{g.label}
              </div>
            ))}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#666' }}>
              <span aria-hidden style={{ width: '12px', height: '12px', borderRadius: '50%', border: '2px dashed #999' }} />přibližná poloha
            </div>
          </div>
        </div>

        <aside style={{ ...card, display: 'flex', flexDirection: 'column', minHeight: 0 }} className="client-map-list" aria-label="Seznam klientů">
          <div style={{ padding: '12px 16px', borderBottom: '1px solid #E0E0E0', fontSize: '14px', fontWeight: 600, color: '#333' }}>
            Klienti ({filtered.length}{hasFilters ? ` z ${clients.length}` : ''})
          </div>
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {loading ? <p style={{ padding: '16px', color: '#666' }}>Načítám…</p>
              : <ClientList clients={filtered} groupFor={groupFor} selectedKey={focus?.key} onSelect={selectClient} />}
          </div>
          {counts.byKraj.length > 0 && (
            <details style={{ borderTop: '1px solid #E0E0E0', padding: '10px 16px', fontSize: '13px' }}>
              <summary style={{ cursor: 'pointer', fontWeight: 600, color: '#333' }}>Podle krajů</summary>
              <ul style={{ listStyle: 'none', margin: '8px 0 0', padding: 0 }}>
                {counts.byKraj.map(([kraj, n]) => (
                  <li key={kraj} style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0', color: '#555' }}><span>{kraj}</span><strong>{n}</strong></li>
                ))}
              </ul>
            </details>
          )}
        </aside>
      </div>

      <style>{`
        .client-map-grid { display: grid; grid-template-columns: minmax(0, 1fr) 340px; gap: 16px; height: calc(100vh - 64px - 48px - 40px); min-height: 520px; }
        .client-map-map > div:first-child { height: 100%; }
        .client-map-list { max-height: 100%; }
        .client-map-marker { background: none; border: none; }
        @media (max-width: 1023px) {
          .client-map-page { padding: 16px !important; }
          .client-map-grid { grid-template-columns: 1fr; height: auto; }
          .client-map-map { height: 60vh; min-height: 380px; }
          .client-map-list { max-height: 50vh; }
        }
      `}</style>
    </div>
  );
};

export default ClientMap;
